import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useI18n } from "../context/I18nContext";
import {
    Wallet,
    Zap,
    ShoppingBasket,
    Banknote,
    LineChart,
    Truck,
    ArrowRight,
    Smartphone,
    Users,
    Store,
    Building2,
    Check,
    MessageCircle,
    Phone,
} from "lucide-react";

const modules = [
    {
        icon: Wallet,
        title: "Merchant Wallet",
        desc: "Float management, transfers, QR/card acceptance and mobile money — all in one place.",
        color: "from-[#FF2D87] to-[#C026D3]",
    },
    {
        icon: Zap,
        title: "Digital Services",
        desc: "Sell airtime, data, electricity, TV, tickets and insurance with instant commission.",
        color: "from-[#22D3EE] to-[#10B981]",
    },
    {
        icon: ShoppingBasket,
        title: "Procurement",
        desc: "Order FMCGs, beverages, household goods and solar products direct from suppliers.",
        color: "from-[#FBBF24] to-[#FF2D87]",
    },
    {
        icon: Banknote,
        title: "Merchant Credit",
        desc: "Float, inventory and device financing — underwritten on your transaction history.",
        color: "from-[#10B981] to-[#22D3EE]",
    },
    {
        icon: LineChart,
        title: "Business Tools",
        desc: "Inventory, bookkeeping, invoicing, analytics and customer management baked in.",
        color: "from-[#C026D3] to-[#22D3EE]",
    },
    {
        icon: Truck,
        title: "Logistics",
        desc: "Local delivery, pickup points and micro-warehousing across urban and rural areas.",
        color: "from-[#FBBF24] to-[#10B981]",
    },
];

const audience = [
    { icon: Store, title: "Merchants", body: "Kiosks, side-hustlers, mobile sellers, community agents.", k: "Run and grow your business from your phone." },
    { icon: Users, title: "Consumers", body: "Underserved households buying essentials.", k: "Access affordable services from a trusted local seller." },
    { icon: Building2, title: "Suppliers", body: "FMCG brands, telcos, insurers, lenders.", k: "Reach Africa\u2019s fragmented informal economy in one channel." },
    { icon: Smartphone, title: "Enterprise", body: "Banks, NGOs, governments, super-agents.", k: "Launch a network without building the infrastructure." },
];

const phases = [
    { tag: "Phase 1", title: "Density First", desc: "Urban and rural area clusters, super-agent acquisition, VAS + wallet." },
    { tag: "Phase 2", title: "Financial Products", desc: "Float, inventory and device financing pilots." },
    { tag: "Phase 3", title: "FMCG Marketplace", desc: "Supplier onboarding, procurement, last-mile logistics." },
    { tag: "Phase 4", title: "Embedded Infrastructure", desc: "API platform, white-label, enterprise distribution." },
];

const problems = [
    "Limited access to inventory",
    "No access to credit",
    "Fragmented supply chains",
    "Cash-heavy operations",
    "No business software",
    "No formal business history",
    "Poor logistics access",
    "Low digital inclusion",
];

const revenue = [
    { title: "Transaction commissions", body: "Airtime, data, electricity, bills, tickets, subscriptions." },
    { title: "Merchant credit", body: "Interest, fees and risk-priced financing." },
    { title: "Procurement margin", body: "Wholesale-to-retail spread on FMCG and goods." },
    { title: "SaaS subscriptions", body: "Pro plans for inventory, analytics and CRM." },
    { title: "Payment processing", body: "QR, card and collections take rate." },
    { title: "Embedded insurance", body: "Funeral, health, device and merchant cover." },
    { title: "Logistics revenue", body: "Delivery, warehousing and pickup aggregation." },
    { title: "API & licensing", body: "Enterprise white-label and infrastructure access." },
];

export default function Landing() {
    const { t } = useI18n();
    return (
        <div className="min-h-screen bg-onego-bg" data-testid="landing-page">
            <Navbar />

            {/* HERO */}
            <section className="relative overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 og-gradient-soft pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7 og-fade-up">
                        <span className="og-chip" data-testid="hero-eyebrow">
                            <span className="h-2 w-2 rounded-full og-gradient" /> Commerce infrastructure for Africa
                        </span>
                        <h1 className="mt-6 font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-onego-navy">
                            One app.
                            <br />
                            <span className="og-gradient-text">Every opportunity.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed">
                            OneGo is the merchant-first operating system for Africa&rsquo;s informal economy &mdash; wallet,
                            digital services, procurement, credit and logistics in a single mobile-first platform.
                        </p>
                        <div className="mt-8 flex flex-wrap items-center gap-3">
                            <Link to="/register" className="og-btn-gradient inline-flex items-center gap-2" data-testid="hero-cta-register">
                                Open a merchant account <ArrowRight size={18} />
                            </Link>
                            <a href="#solution" className="og-btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-explore">
                                Explore the platform
                            </a>
                        </div>
                        <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                            <div className="flex items-center gap-2"><Check size={16} className="text-onego-green" /> Mobile-first</div>
                            <div className="flex items-center gap-2"><Check size={16} className="text-onego-green" /> Offline resilient</div>
                            <div className="flex items-center gap-2"><Check size={16} className="text-onego-green" /> Built for super-agents</div>
                        </div>
                    </div>
                    <div className="lg:col-span-5 og-fade-up og-delay-2">
                        <div className="relative">
                            <div className="absolute -inset-6 og-gradient opacity-20 blur-2xl rounded-[3rem]" />
                            <div className="relative og-card overflow-hidden p-0">
                                <img
                                    src="https://images.pexels.com/photos/30019730/pexels-photo-30019730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
                                    alt="African merchant in a market"
                                    className="w-full h-[460px] object-cover"
                                />
                                <div className="absolute left-4 bottom-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Today&rsquo;s GMV</p>
                                            <p className="text-2xl font-display font-extrabold text-onego-navy">KES 12,480</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Commission</p>
                                            <p className="text-2xl font-display font-extrabold og-gradient-text">+ KES 612</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex gap-2">
                                        <span className="og-chip">Airtime</span>
                                        <span className="og-chip">Electricity</span>
                                        <span className="og-chip">Inventory</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Trust strip */}
                <div className="relative border-y border-slate-200 bg-white/60 backdrop-blur">
                    <div className="og-marquee-track py-4 text-onego-navy/70 font-semibold uppercase text-xs tracking-widest">
                        {Array.from({ length: 2 }).map((_, dup) => (
                            <div key={dup} className="flex gap-12 px-8 whitespace-nowrap">
                                <span>Kiosks</span>
                                <span>&bull;</span>
                                <span>Mobile money agents</span>
                                <span>&bull;</span>
                                <span>Side-hustlers</span>
                                <span>&bull;</span>
                                <span>Urban retailers</span>
                                <span>&bull;</span>
                                <span>Rural retailers</span>
                                <span>&bull;</span>
                                <span>Super-agents</span>
                                <span>&bull;</span>
                                <span>Rural distributors</span>
                                <span>&bull;</span>
                                <span>FMCG suppliers</span>
                                <span>&bull;</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROBLEM */}
            <section id="problem" className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="problem-section">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="og-chip">The problem</span>
                        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-onego-navy">
                            Informal merchants power the economy. The system fails them.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            Across Africa, hundreds of millions of transactions happen in kiosks and roadside
                            stands every day. Yet these merchants are invisible to capital, software and supply chains.
                        </p>
                        <div className="mt-8 og-divider-gradient w-32" />
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-2 gap-4">
                        {problems.map((p, i) => (
                            <div key={p} className={`og-card p-6 og-fade-up og-delay-${(i % 5) + 1}`}>
                                <div className="h-9 w-9 rounded-full og-gradient flex items-center justify-center text-white font-bold">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <p className="mt-4 font-display font-bold text-onego-navy">{p}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SOLUTION */}
            <section id="solution" className="bg-white border-y border-slate-200" data-testid="solution-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="flex items-end justify-between flex-wrap gap-6">
                        <div>
                            <span className="og-chip">The solution</span>
                            <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-onego-navy max-w-2xl">
                                Six modules. One operating system.
                            </h2>
                        </div>
                        <p className="max-w-md text-slate-600">
                            We don&rsquo;t build another wallet or POS. We build the infrastructure beneath the entire
                            informal commerce stack.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {modules.map((m, i) => {
                            const Icon = m.icon;
                            return (
                                <div key={m.title} className={`og-card p-8 og-fade-up og-delay-${(i % 5) + 1}`} data-testid={`module-card-${i}`}>
                                    <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${m.color} flex items-center justify-center text-white shadow-md`}>
                                        <Icon size={22} />
                                    </div>
                                    <h3 className="mt-5 font-display text-2xl font-bold text-onego-navy">{m.title}</h3>
                                    <p className="mt-2 text-slate-600 leading-relaxed">{m.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHO IT'S FOR */}
            <section id="who" className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="audience-section">
                <span className="og-chip">Who it&rsquo;s for</span>
                <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-onego-navy max-w-3xl">
                    Built for everyone touching informal commerce.
                </h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {audience.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <div key={a.title} className={`og-card p-7 og-fade-up og-delay-${(i % 5) + 1}`}>
                                <div className="h-11 w-11 rounded-2xl bg-onego-navy text-white flex items-center justify-center">
                                    <Icon size={20} />
                                </div>
                                <h3 className="mt-4 font-display text-xl font-bold text-onego-navy">{a.title}</h3>
                                <p className="mt-1 text-sm text-slate-500">{a.body}</p>
                                <p className="mt-4 text-sm font-semibold text-onego-navy">&ldquo;{a.k}&rdquo;</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* USSD / WHATSAPP */}
            <section className="bg-white border-t border-slate-200" data-testid="ussd-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <span className="og-chip">{t.ussd.chip}</span>
                        <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-onego-navy">
                            {t.ussd.title}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">
                            {t.ussd.body}
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3">
                            <div className="og-card p-5 inline-flex items-center gap-3">
                                <Phone size={20} className="text-onego-navy" />
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t.ussd.ussd_label}</p>
                                    <p className="font-display text-2xl font-extrabold og-gradient-text">*483*OG#</p>
                                </div>
                            </div>
                            <div className="og-card p-5 inline-flex items-center gap-3">
                                <MessageCircle size={20} className="text-onego-navy" />
                                <div>
                                    <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t.ussd.whatsapp_label}</p>
                                    <p className="font-display text-2xl font-extrabold og-gradient-text">+254 720 ONEGO</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="og-card p-8 bg-onego-navy text-white">
                            <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">USSD preview</p>
                            <pre className="mt-3 font-mono text-sm leading-relaxed whitespace-pre-wrap text-white/90">
{`OneGo Africa

1. Sell airtime
2. Sell electricity
3. Wallet balance
4. Top-up wallet
5. Apply for float credit
6. Recent sales
0. Exit

Reply with option:`}
                            </pre>
                        </div>
                    </div>
                </div>
            </section>

            {/* REVENUE MODEL */}
            <section className="bg-white border-y border-slate-200" data-testid="revenue-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <span className="og-chip">Revenue model</span>
                            <h2 className="mt-4 font-display font-extrabold text-4xl tracking-tight text-onego-navy">
                                Eight compounding revenue streams.
                            </h2>
                            <p className="mt-4 text-slate-600">
                                Frequency from VAS. Margin from credit. Scale from procurement. Defensibility from
                                infrastructure.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {revenue.map((r) => (
                                <div key={r.title} className="og-card p-6">
                                    <p className="font-display text-lg font-bold text-onego-navy">{r.title}</p>
                                    <p className="mt-1 text-sm text-slate-600">{r.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ROADMAP */}
            <section id="roadmap" className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="roadmap-section">
                <span className="og-chip">Roadmap</span>
                <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-onego-navy max-w-3xl">
                    Density first. Then financial products. Then the network.
                </h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {phases.map((p, i) => (
                        <div key={p.tag} className="og-card p-7 relative overflow-hidden">
                            <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full og-gradient opacity-${20 + i * 10}`} />
                            <span className="og-chip">{p.tag}</span>
                            <h3 className="mt-4 font-display text-2xl font-bold text-onego-navy">{p.title}</h3>
                            <p className="mt-2 text-slate-600 leading-relaxed">{p.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* SCALE STATS */}
            <section className="bg-onego-navy text-white" data-testid="scale-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-5">
                            <span className="og-chip" style={{ background: "rgba(255,255,255,0.08)", color: "white" }}>
                                Scale projections
                            </span>
                            <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight">
                                A network designed to <span className="og-gradient-text">compound</span>.
                            </h2>
                            <p className="mt-4 text-white/70 max-w-md">
                                Average merchant revenue $105/month. Density unlocks credit. Credit unlocks GMV. GMV
                                unlocks supply.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-6">
                            {[
                                { v: "10K", l: "merchants", r: "$1M+ MRR" },
                                { v: "100K", l: "merchants", r: "$10M+ MRR" },
                                { v: "1M", l: "merchants", r: "$100M+ MRR" },
                            ].map((s, i) => (
                                <div key={s.v} className={`p-7 rounded-3xl border border-white/10 bg-white/5 og-fade-up og-delay-${i + 1}`}>
                                    <p className="font-display text-5xl font-black og-gradient-text">{s.v}</p>
                                    <p className="mt-1 text-white/70 capitalize">{s.l}</p>
                                    <p className="mt-4 text-sm font-bold">{s.r}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="cta-section">
                <div className="relative overflow-hidden rounded-3xl p-1 og-gradient">
                    <div className="rounded-[1.4rem] bg-white p-10 md:p-16">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-7">
                                <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight text-onego-navy">
                                    Be one of the first merchants on OneGo.
                                </h2>
                                <p className="mt-4 text-slate-600 max-w-xl">
                                    Open a free merchant account. Top up your wallet. Start earning commission today.
                                </p>
                            </div>
                            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
                                <Link to="/register" className="og-btn-gradient inline-flex items-center gap-2" data-testid="cta-register">
                                    Get started <ArrowRight size={18} />
                                </Link>
                                <Link to="/login" className="og-btn-ghost" data-testid="cta-login">
                                    I already have an account
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
